import { ecosystemItems } from "views/landing/constants";
import Container from "../Container";
import SectionHeader from "../SectionHeader";
import EcosystemCard from "./components/EcosystemCard";
import middleLogo from "../../../../assets/landing/animation-middle-logo.png";
import logoMobile from "../../../../assets/landing/logo-mobile.png";
import agntLogo from "../../../../assets/landing/ecosystem-ellipse.png";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useAnimation,
  useTransform,
  useSpring,
  useScroll,
} from "framer-motion";

const Ecosystem = () => {
  const refMobile = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const controlsWheel = useAnimation();

  const isMobileInView = useInView(refMobile, { once: true });
  const isSectionInView = useInView(sectionRef, { once: true });

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stepPositions, setStepPositions] = useState<number[]>([]);

  const controlsMobile = useAnimation();
  const controlsDesktop = useAnimation();

  const controlsLogoRotation = useAnimation();
  const [rotation, setRotation] = useState(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 1080]);
  const y = useTransform(scrollYProgress, [0, 1], ["0px", "900vw"]);

  const smoothY = useSpring(y, { stiffness: 400, damping: 30 });

  const handleHover = async () => {
    const randomRotation = Math.floor(Math.random() * 16) + 60;
    const newRotation = rotation + randomRotation;

    await controlsLogoRotation.start({
      rotate: newRotation,
      transition: { duration: 0.4, ease: "easeInOut" },
    });

    setRotation(newRotation);
  };

  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
      controlsWheel.start("visible");
    }
  }, [isSectionInView, controlsDesktop, controlsWheel]);

  useEffect(() => {
    if (containerRef.current) {
      const containerHeight = (containerRef.current as HTMLDivElement)
        .offsetHeight;
      const numberOfSteps = 4;

      const stepSize = containerHeight / numberOfSteps;

      const positions = Array.from(
        { length: numberOfSteps },
        (_, index) => stepSize * index
      );
      setStepPositions(positions);
    }
  }, []);

  useEffect(() => {
    if (isMobileInView && stepPositions.length > 0) {
      const runAnimation = async () => {
        while (true) {
          setCurrentStep(0);
          await controlsMobile.start({
            y: `${stepPositions[0]}px`,
            transition: { duration: 0.1 }, // Faster move
          });

          await controlsMobile.start({
            y: `${stepPositions[1]}px`,
            rotate: 180,
            transition: { duration: 1.5, delay: 1.2 }, // keep rotation delay
          });
          setCurrentStep(1);

          await controlsMobile.start({
            y: `${stepPositions[2]}px`,
            rotate: 360,
            transition: { duration: 1.5, delay: 1.2 },
          });
          setCurrentStep(2);

          await controlsMobile.start({
            y: `${stepPositions[3]}px`,
            rotate: 480,
            transition: { duration: 1.5, delay: 1.2 },
          });
          setCurrentStep(3);

          await controlsMobile.start({
            y: `${stepPositions[4]}px`,
            rotate: 600,
            transition: { duration: 1.5, delay: 1.2 },
          });
          setCurrentStep(4);

          await controlsMobile.start({
            y: "0px",
            rotate: 0,
            transition: { duration: 0.6, ease: "easeInOut", delay: 1 },
          });
          setCurrentStep(-1);
        }
      };

      runAnimation();
    }
  }, [isMobileInView, controlsMobile, stepPositions]);

  // Desktop section entrance animation
  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
    }
  }, [isSectionInView, controlsDesktop]);

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.5, // Delay between each card
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const wheelVariants = {
    hidden: { opacity: 0, scale: 0.75 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.5, ease: "easeOut", delay: 1 },
    },
  };

  return (
    <div ref={sectionRef}>
      <Container
        sectionClasses="mb-[9vw] max-lg:mb-[26.66vw]"
        animationControls={controlsDesktop}
      >
        <SectionHeader
          title="Ecosystem"
          desc="Learn more about AGNT"
          sectionHeaderClasses="mb-[1.714vw] max-lg:mb-[6.4vw]"
        />
        <div className="relative z-10 max-lg:grid grid-cols-[16vw_1fr] max-lg:gap-[3.2vw]">
          <div
            ref={containerRef}
            className="h-full lg:hidden custom-shadow relative bg-[#F3F3F8] rounded-full gradient-border w-full"
          >
            <motion.img
              ref={refMobile}
              src={logoMobile}
              alt={"mobile logo"}
              animate={controlsMobile}
              className="w-[16.26vw] h-[16] scale-150"
            />
          </div>
          <motion.div
            className="grid grid-cols-2 max-lg:grid-cols-1 max-lg:gap-[3.2vw] gap-[0.8vw] justify-center relative"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {ecosystemItems.map(
              (
                { desc, itemCardImg, step, stepIcon, title, descWidth },
                index
              ) => (
                <motion.div key={title} variants={cardVariants}>
                  <EcosystemCard
                    cardBgImg={itemCardImg}
                    desc={desc}
                    stepIcon={stepIcon}
                    stepInfo={step}
                    title={title}
                    isLeftSide={(index + 1) % 2 === 1}
                    descWidth={descWidth}
                    step={index}
                    currentStep={currentStep}
                    hoverRotation={handleHover}
                  />
                </motion.div>
              )
            )}

            <div className="absolute shadow-xl  top-1/2 left-1/2 max-lg:hidden flex items-center justify-center rounded-full -translate-y-[51%] -translate-x-1/2 origin-center">
              <motion.div
                variants={wheelVariants}
                animate={controlsLogoRotation}
                style={{ transformOrigin: "center center" }}
                className="relative w-full h-full  flex items-center justify-center"
              >
                <img
                  className="!w-[18.22vw] h-auto"
                  src={agntLogo}
                  alt="logo decoration ellipse"
                />

                <img
                  src={middleLogo}
                  alt="AGNT.hub logo"
                  className="size-[8.928vw] absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </Container>
    </div>
  );
};

export default Ecosystem;
